export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
}

/**
 * Custom event dispatched on `window` when the user (or another part of the
 * app) requests the cookie preferences modal. The custom CookieBanner
 * component listens for this and opens its modal.
 */
export const COOKIE_PREFERENCES_EVENT = 'agb:cookie-preferences:open';

/**
 * Parse consent state directly from the raw `cc_cookie` document cookie.
 * Safe to call at boot-time before the CookieBanner component has mounted.
 *
 * The cookie format is kept backwards-compatible with the previous
 * vanilla-cookieconsent schema ({ categories: ['necessary', 'analytics', ...] }).
 */
export function readConsentFromCookie(): ConsentState {
  if (typeof document === 'undefined') {
    return { analytics: false, marketing: false };
  }
  const match = document.cookie.match(/(?:^|;\s*)cc_cookie=([^;]*)/);
  if (!match?.[1]) {
    return { analytics: false, marketing: false };
  }
  try {
    const decoded = decodeURIComponent(match[1]);
    const parsed: unknown = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object' && 'categories' in parsed) {
      const categories = (parsed as { categories: string[] }).categories;
      return {
        analytics: Array.isArray(categories) && categories.includes('analytics'),
        marketing: Array.isArray(categories) && categories.includes('marketing'),
      };
    }
  } catch {
    // Malformed cookie — treat as no consent
  }
  return { analytics: false, marketing: false };
}

/**
 * Open the cookie preferences modal. Works from anywhere (footer, legal
 * page, etc.) — dispatches a window event that the CookieBanner picks up.
 * If the banner hasn't mounted yet, the event is silently ignored.
 */
export function showPreferencesModal(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_EVENT));
}
